import React from 'react';
import { 
  X, 
  History, 
  Calendar,
  Award,
  TrendingUp,
  MapPin
} from 'lucide-react';

export const PerformanceHistoryModal = ({
  isOpen,
  onClose,
  routine,
  selectedHistoricalEvent,
  onEventSelect
}) => {
  if (!isOpen) return null;

  // Calculate improvement trend
  const calculateTrend = (performances) => {
    if (performances.length < 2) return 0;
    const latest = performances[performances.length - 1].score;
    const previous = performances[performances.length - 2].score;
    return ((latest - previous) / previous) * 100;
  };

  const trend = calculateTrend(routine.performanceHistory);

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
        <div className="relative w-full max-w-2xl bg-base-100 rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-base-200">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-semibold text-base-content">
                Performance History
              </h3>
            </div>
            <button
              onClick={onClose}
              className="btn btn-ghost btn-sm btn-circle"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Stats Overview */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-base-200 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-base-content/70 mb-1">
                  <Award className="w-4 h-4" />
                  <span className="text-sm">Best Score</span>
                </div>
                <div className="text-xl font-semibold text-base-content">
                  {Math.max(...routine.performanceHistory.map(p => p.score))}
                </div>
              </div>

              <div className="bg-base-200 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-base-content/70 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">Improvement</span>
                </div>
                <div className={`text-xl font-semibold ${
                  trend > 0 ? 'text-success' : trend < 0 ? 'text-error' : 'text-base-content'
                }`}>
                  {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                </div>
              </div>

              <div className="bg-base-200 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-base-content/70 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Total Events</span>
                </div>
                <div className="text-xl font-semibold text-base-content">
                  {routine.performanceHistory.length}
                </div>
              </div>
            </div>

            {/* Performance List */}
            <div className="space-y-3">
              {routine.performanceHistory.map((performance, index) => (
                <button
                  key={index}
                  onClick={() => onEventSelect(performance.eventName)}
                  className={`w-full p-4 rounded-lg text-left transition-colors ${
                    selectedHistoricalEvent === performance.eventName
                      ? 'bg-primary/10'
                      : 'bg-base-200 hover:bg-base-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-base-content">
                        {performance.eventName}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-sm text-base-content/60">
                        <MapPin className="w-4 h-4" />
                        {performance.location}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-primary">
                        {performance.score}
                      </div>
                      {performance.placement && (
                        <div className="text-sm text-base-content/60">
                          {performance.placement} Place
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Score Breakdown */}
                  {performance.scoreBreakdown && (
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      {Object.entries(performance.scoreBreakdown).map(([category, score]) => (
                        <div key={category} className="text-center p-2 bg-base-300 rounded">
                          <div className="text-xs text-base-content/60">{category}</div>
                          <div className="text-sm font-medium text-base-content">{score}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>

            {routine.performanceHistory.length === 0 && (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-base-content/20 mx-auto mb-2" />
                <p className="text-base-content/60">No performance history available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};